import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  FaShieldAlt,
  FaSpinner,
  FaCheckCircle,
  FaArrowLeft,
} from 'react-icons/fa';

import api from '../services/api';
import { useAuth } from '../context/AuthContext';

const Setup2FA = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { login } = useAuth();

  const mobile = location.state?.mobile || '';
  const userId = location.state?.userId || '';

  const [secret, setSecret] = useState('');
  const [qrUrl, setQrUrl] = useState('');
  const [code, setCode] = useState('');
  const [setupLoading, setSetupLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [verified, setVerified] = useState(false);

  /* =========================================================
     LOAD 2FA SETUP
  ========================================================= */

  useEffect(() => {
    const load2FASetup = async () => {
      if (!userId) {
        toast.error('User information is missing.');
        navigate('/login', { replace: true });
        return;
      }

      try {
        setSetupLoading(true);

        console.log(
          'STARTING 2FA SETUP FOR USER:',
          userId
        );

        const res = await api.post(
          `/api/auth/enable-2fa?userId=${userId}`
        );

        console.log(
          'ENABLE 2FA RESPONSE:',
          res.data
        );

        if (!res.data) {
          toast.error(
            'Unable to initialize 2FA setup.'
          );
          return;
        }

        setSecret(res.data.secret || '');
        setQrUrl(res.data.qrUrl || '');

      } catch (error) {
        console.error(
          'ENABLE 2FA ERROR:',
          error?.response?.data || error
        );

        toast.error(
          error?.response?.data?.message ||
          'Unable to start 2FA setup'
        );
      } finally {
        setSetupLoading(false);
      }
    };

    load2FASetup();
  }, [userId, navigate]);

  /* =========================================================
     VERIFY AUTHENTICATOR CODE
  ========================================================= */

  const verifySetup = async () => {
    if (loading) return;

    const cleanCode = code
      .replace(/\D/g, '')
      .slice(0, 6);

    if (cleanCode.length !== 6) {
      toast.error(
        'Enter a valid 6-digit authenticator code'
      );
      return;
    }

    if (!mobile) {
      toast.error(
        'Mobile number is missing'
      );
      return;
    }

    try {
      setLoading(true);

      console.log(
        'VERIFYING INITIAL 2FA:',
        {
          mobile,
          code: cleanCode,
        }
      );

      const res = await api.post(
        '/api/auth/verify-2fa',
        {
          mobile,
          code: Number(cleanCode),
        }
      );

      console.log(
        'VERIFY 2FA RESPONSE:',
        res.data
      );

      if (!res.data?.success) {
        toast.error(
          res.data?.message ||
          'Invalid authenticator code'
        );
        return;
      }

      if (!res.data?.token) {
        toast.error(
          'Authentication token not received'
        );
        return;
      }

      /*
       * Save JWT.
       */
      await login(
        res.data.token,
        res.data.user || {
          id: res.data.userId,
          role: res.data.role,
          mobile,
        }
      );

      setVerified(true);

      toast.success(
        '2FA setup completed successfully'
      );

      setTimeout(() => {
        navigate('/dashboard', {
          replace: true,
        });
      }, 800);

    } catch (error) {
      console.error(
        'VERIFY 2FA ERROR:',
        error?.response?.data || error
      );

      toast.error(
        error?.response?.data?.message ||
        '2FA verification failed'
      );
    } finally {
      setLoading(false);
    }
  };

  /* =========================================================
     UI
  ========================================================= */

  return (
    <div
      style={{
        minHeight: '100vh',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#F7F4EE',
        fontFamily:
          "'Inter', system-ui, sans-serif",
        padding: '24px',
        boxSizing: 'border-box',
      }}
    >

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,500;9..144,600&family=Inter:wght@400;500;600;700&family=JetBrains+Mono:wght@500;700&display=swap');

        @keyframes rotate {
          from {
            transform: rotate(0deg);
          }

          to {
            transform: rotate(360deg);
          }
        }

        @keyframes stampIn {
          0% {
            transform: scale(0.7);
            opacity: 0;
          }

          100% {
            transform: scale(1);
            opacity: 1;
          }
        }

        .setup-input:focus {
          outline: none;
          border-color: #C9A227 !important;
          box-shadow:
            0 0 0 3px rgba(201,162,39,0.15);
        }

        .setup-btn:hover:not(:disabled) {
          background: #0F2545 !important;
        }

        .back-btn:hover {
          color: #16325C !important;
        }

        @media (max-width: 520px) {
          .setup-card {
            padding: 28px 22px !important;
          }

          .qr-image {
            width: 190px !important;
            height: 190px !important;
          }
        }
      `}</style>

      <div
        className="setup-card"
        style={{
          width: '100%',
          maxWidth: '480px',
          background: '#FFFFFF',
          borderRadius: '18px',
          padding: '38px 40px',
          boxShadow:
            '0 24px 60px -20px rgba(16,35,63,0.35)',
          textAlign: 'center',
          boxSizing: 'border-box',
        }}
      >

        {!verified ? (
          <>

            {/* =================================================
                HEADER ICON
            ================================================= */}

            <div
              style={{
                width: 66,
                height: 66,
                borderRadius: '50%',
                background:
                  'linear-gradient(160deg,#16325C,#0F2545)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 18px',
                border:
                  '1.5px solid #C9A227',
              }}
            >
              <FaShieldAlt
                size={26}
                color="#C9A227"
              />
            </div>

            <p
              style={{
                color: '#C9A227',
                fontSize: 11.5,
                letterSpacing: '0.18em',
                textTransform: 'uppercase',
                fontWeight: 600,
                margin: '0 0 7px',
              }}
            >
              Security Setup
            </p>

            <h1
              style={{
                fontFamily:
                  "'Fraunces', serif",
                fontWeight: 600,
                fontSize: 25,
                color: '#10233F',
                margin: '0 0 10px',
              }}
            >
              Set Up Two-Factor Authentication
            </h1>

            <p
              style={{
                color: '#697386',
                fontSize: 14,
                lineHeight: 1.6,
                margin:
                  '0 auto 24px',
                maxWidth: 350,
              }}
            >
              Secure your Corporator account
              with Google Authenticator.
            </p>

            {setupLoading ? (

              /* =================================================
                 LOADING
              ================================================= */

              <div
                style={{
                  padding: '35px 0',
                }}
              >
                <FaSpinner
                  size={25}
                  color="#16325C"
                  style={{
                    animation:
                      'rotate 0.8s linear infinite',
                  }}
                />

                <p
                  style={{
                    color: '#697386',
                    marginTop: 12,
                    fontSize: 14,
                  }}
                >
                  Preparing secure setup...
                </p>
              </div>

            ) : (

              <>

                {/* =================================================
                    STEP 1
                ================================================= */}

                <div
                  style={{
                    textAlign: 'left',
                    marginBottom: 10,
                    fontSize: 12,
                    fontWeight: 700,
                    color: '#38445C',
                  }}
                >
                  STEP 1 — SCAN QR CODE
                </div>

                {qrUrl ? (

                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      marginBottom: 16,
                    }}
                  >
                    <img
                      className="qr-image"
                      src={`https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(
                        qrUrl
                      )}`}
                      alt="Google Authenticator QR Code"
                      style={{
                        width: 220,
                        height: 220,
                        border:
                          '1px solid #DDE2EA',
                        borderRadius: 10,
                        padding: 8,
                        background: '#FFFFFF',
                        boxSizing:
                          'border-box',
                      }}
                    />
                  </div>

                ) : (

                  <div
                    style={{
                      padding: 20,
                      background: '#FEF2F2',
                      borderRadius: 10,
                      color: '#991B1B',
                      fontSize: 13,
                      marginBottom: 18,
                    }}
                  >
                    QR code could not be generated.
                  </div>

                )}

                <p
                  style={{
                    color: '#697386',
                    fontSize: 13,
                    lineHeight: 1.5,
                    margin:
                      '0 auto 18px',
                    maxWidth: 330,
                  }}
                >
                  Open Google Authenticator
                  on your phone and scan the
                  QR code above.
                </p>

                {/* =================================================
                    MANUAL SECRET
                ================================================= */}

                {secret && (
                  <div
                    style={{
                      background: '#F7F4EE',
                      borderRadius: 10,
                      padding:
                        '12px 14px',
                      marginBottom: 22,
                      textAlign: 'left',
                    }}
                  >
                    <div
                      style={{
                        fontSize: 11,
                        color: '#697386',
                        marginBottom: 6,
                        fontWeight: 600,
                      }}
                    >
                      Can't scan?
                      Enter this key manually:
                    </div>

                    <div
                      style={{
                        fontFamily:
                          "'JetBrains Mono', monospace",
                        fontSize: 13,
                        fontWeight: 700,
                        color: '#10233F',
                        letterSpacing: 1,
                        wordBreak:
                          'break-all',
                        lineHeight: 1.6,
                      }}
                    >
                      {secret}
                    </div>
                  </div>
                )}

                {/* =================================================
                    STEP 2
                ================================================= */}

                <div
                  style={{
                    textAlign: 'left',
                    marginBottom: 8,
                    fontSize: 12,
                    fontWeight: 700,
                    color: '#38445C',
                  }}
                >
                  STEP 2 — ENTER AUTHENTICATOR CODE
                </div>

                <input
                  className="setup-input"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  value={code}
                  disabled={loading}
                  placeholder="000000"
                  onChange={(e) => {
                    setCode(
                      e.target.value
                        .replace(/\D/g, '')
                        .slice(0, 6)
                    );
                  }}
                  onKeyDown={(e) => {
                    if (
                      e.key === 'Enter'
                    ) {
                      verifySetup();
                    }
                  }}
                  style={{
                    width: '100%',
                    boxSizing: 'border-box',
                    padding: '14px',
                    textAlign: 'center',
                    fontFamily:
                      "'JetBrains Mono', monospace",
                    fontSize: 23,
                    fontWeight: 700,
                    letterSpacing: 7,
                    border:
                      '1.5px solid #DDE2EA',
                    borderRadius: 10,
                    background: '#FCFBF8',
                    color: '#10233F',
                    marginBottom: 16,
                  }}
                />

                {/* =================================================
                    VERIFY BUTTON
                ================================================= */}

                <button
                  className="setup-btn"
                  onClick={verifySetup}
                  disabled={
                    loading ||
                    code.length !== 6
                  }
                  style={{
                    width: '100%',
                    border: 'none',
                    borderRadius: 10,
                    padding: '14px 0',
                    background:
                      '#16325C',
                    color: '#F7F4EE',
                    fontSize: 14.5,
                    fontWeight: 600,
                    cursor:
                      loading ||
                      code.length !== 6
                        ? 'default'
                        : 'pointer',
                    display: 'flex',
                    alignItems:
                      'center',
                    justifyContent:
                      'center',
                    gap: 8,
                    opacity:
                      loading ||
                      code.length !== 6
                        ? 0.6
                        : 1,
                  }}
                >
                  {loading ? (
                    <>
                      <FaSpinner
                        size={14}
                        style={{
                          animation:
                            'rotate 0.8s linear infinite',
                        }}
                      />

                      Verifying...
                    </>
                  ) : (
                    <>
                      <FaShieldAlt
                        size={14}
                      />

                      Enable 2FA & Continue
                    </>
                  )}
                </button>

              </>
            )}

            {/* =================================================
                BACK
            ================================================= */}

            <button
              className="back-btn"
              type="button"
              onClick={() =>
                navigate('/login')
              }
              style={{
                marginTop: 20,
                background: 'none',
                border: 'none',
                color: '#697386',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                fontSize: 13,
              }}
            >
              <FaArrowLeft size={11} />
              Back to login
            </button>

          </>

        ) : (

          /* =====================================================
             SUCCESS
          ===================================================== */

          <div
            style={{
              padding: '25px 0',
              animation:
                'stampIn 0.5s ease-out',
            }}
          >
            <FaCheckCircle
              size={50}
              color="#1F7A4D"
            />

            <h2
              style={{
                fontFamily:
                  "'Fraunces', serif",
                fontSize: 23,
                color: '#10233F',
                margin:
                  '16px 0 8px',
              }}
            >
              2FA Enabled
            </h2>

            <p
              style={{
                color: '#697386',
                fontSize: 14,
                lineHeight: 1.5,
              }}
            >
              Your Corporator account is now
              protected with two-factor
              authentication.
            </p>

            <p
              style={{
                color: '#697386',
                fontSize: 13,
                marginTop: 16,
              }}
            >
              Taking you to your dashboard...
            </p>
          </div>
        )}

      </div>
    </div>
  );
};

export default Setup2FA;